import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ShoppingCart,
  Plus,
  Search,
  Eye,
  CheckCircle2,
  Package,
  Clock,
  Loader2,
  DollarSign
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import useCustomFields from '../hooks/useCustomFields';
import CustomFieldCell from '../components/custom-fields/CustomFieldCell';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusColor = (status) => {
  const colors = {
    'OPEN': 'bg-orange-100 text-orange-800',
    'PARTIALLY_RECEIVED': 'bg-yellow-100 text-yellow-800',
    'FULLY_RECEIVED': 'bg-green-100 text-green-800',
    'PARTIALLY_INVOICED': 'bg-orange-100 text-orange-800',
    'FULLY_INVOICED': 'bg-green-100 text-green-800',
    'CLOSED': 'bg-gray-100 text-gray-800',
    'CANCELLED': 'bg-red-100 text-red-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
};

const calculatePOTotal = (po) => {
  if (!po.items) return 0;
  return po.items.reduce((total, item) => {
    const itemTotal = item.quantity * item.net_price / item.price_unit;
    const taxAmount = itemTotal * (item.tax_percentage / 100);
    return total + itemTotal + taxAmount;
  }, 0);
};

const POList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState([]);
  const [filteredPos, setFilteredPos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const { fields: listCustomFields } = useCustomFields('purchase_order', { listViewOnly: true });

  useEffect(() => {
    loadPOs();
  }, []);


  const loadPOs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/purchase-orders`);
      setPos(response.data);
    } catch (error) {
      console.error('Error loading POs:', error);
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const filterPOs = useCallback(() => {
    let filtered = [...pos];
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(po => po.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(po =>
        po.po_number.toLowerCase().includes(term) ||
        po.vendor_id.toLowerCase().includes(term) ||
        po.created_by.toLowerCase().includes(term)
      );
    }
    setFilteredPos(filtered);
  }, [pos, searchTerm, statusFilter]);

  useEffect(() => {
    filterPOs();
  }, [filterPOs]);

  const stats = {
    total: pos.length,
    open: pos.filter(po => po.status === 'OPEN').length,
    received: pos.filter(po => po.status === 'FULLY_RECEIVED').length,
    invoiced: pos.filter(po => po.status === 'FULLY_INVOICED').length
  };

  const columns = [
    { key: 'po_number', header: 'PO Number', filterable: true, accessor: (po) => po.po_number,
      render: (po) => <span className="text-sm font-medium text-orange-600">{po.po_number}</span> },
    { key: 'vendor_id', header: 'Vendor', filterable: true, accessor: (po) => po.vendor_id },
    { key: 'status', header: 'Status',
      render: (po) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(po.status)}`}>
          {po.status.replace(/_/g, ' ')}
        </span>
      ) },
    { key: 'items', header: 'Items', accessor: (po) => `${po.items?.length || 0} Item${po.items?.length !== 1 ? 's' : ''}` },
    { key: 'total', header: 'Total Value', align: 'right',
      render: (po) => (
        <div className="flex items-center justify-end text-sm text-gray-900">
          <DollarSign className="w-4 h-4 mr-1 text-gray-400" />{calculatePOTotal(po).toFixed(2)}
        </div>
      ) },
    { key: 'created_by', header: 'Created By', accessor: (po) => po.created_by },
    { key: 'created_at', header: 'Created At', accessor: (po) => formatDate(po.created_at) },
    ...listCustomFields.map((f) => ({
      key: `cf_${f.fieldname}`, header: f.label,
      render: (po) => (
        <span data-testid={`po-cf-${f.fieldname}-${po.po_number}`}>
          <CustomFieldCell field={f} value={po.custom_data?.[f.fieldname]} />
        </span>
      ),
    })),
    { key: 'actions', header: 'Actions', align: 'right',
      render: (po) => (
        <Button variant="ghost" size="sm" onClick={() => navigate(`/p2p/po-view/${po.po_number}`)} className="text-orange-600 hover:text-orange-700">
          <Eye className="w-4 h-4 mr-1" />View
        </Button>
      ) },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading purchase orders...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="w-7 h-7 text-orange-600" />
              Purchase Orders
            </h1>
            <p className="text-sm text-gray-600 mt-1">{filteredPos.length} of {pos.length} purchase orders</p>
          </div>
          {canWrite('purchase_order') && (
          <Button onClick={() => navigate('/p2p/po-create')} className="bg-orange-600 hover:bg-orange-700">
            <Plus className="w-4 h-4 mr-2" />
            Create PO
          </Button>
          )}
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by PO number, vendor, or creator..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="ALL">All Status</option>
              <option value="OPEN">Open</option>
              <option value="PARTIALLY_RECEIVED">Partially Received</option>
              <option value="FULLY_RECEIVED">Fully Received</option>
              <option value="PARTIALLY_INVOICED">Partially Invoiced</option>
              <option value="FULLY_INVOICED">Fully Invoiced</option>
              <option value="CLOSED">Closed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total POs</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Open</p>
                <p className="text-2xl font-bold text-orange-700">{stats.open}</p>
              </div>
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Received</p>
                <p className="text-2xl font-bold text-green-700">{stats.received}</p>
              </div>
              <Package className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Invoiced</p>
                <p className="text-2xl font-bold text-green-700">{stats.invoiced}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>

        {/* PO List */}
        <DataTable
          testid="po-table"
          columns={columns}
          data={filteredPos}
          rowKey={(po) => po.po_number}
          emptyText="No purchase orders found"
          emptyHint="Create your first purchase order to get started"
          rowProps={(po) => ({ 'data-testid': `po-${po.po_number}` })}
        />
      </div>
    </Layout>
  );
};

export default POList;
